/* eslint-disable jsx-a11y/no-noninteractive-element-interactions */
import MUISelectInput from '@brands/Components/Inputs/MuiSelectInput/MUISelectInput';
import { selectAuth } from '@brands/store/selectors/auth';
import { selectCurrentPatientState } from '@brands/store/selectors/currentPatientState';
import { setPatientState } from '@brands/store/slices/currentPatientState';
import { customStylesDashboard } from '@brands/Utils/customStyles';
import { Button } from '@mui/material';
import React, { FC, useEffect, useMemo, useState } from 'react';
import Modal, { Styles } from 'react-modal';

import { useWindowSize } from '../../../../hooks';
import { useAppDispatch, useAppSelector } from '../../../../hooks/useReduxHook';
import { getCurrentPState, setCurrentPatientState } from '../../../../Utils/localStorage';
import { stateOptionsWithoutAbbreviation } from '../../../../Utils/selectOptions';
import closeIcon from '../../Patient/components/closeIconSVG.svg';
import styles from './styles.module.scss';

interface StateModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const customStyles = {
  overlay: {
    backgroundColor: 'rgba(0, 0, 0, 0.25)',
    zIndex: '999',
  },
  content: {
    top: '50%',
    left: '50%',
    width: '100%',
    height: '100%',
    marginRight: '-50%',
    transform: 'translate(-50%, -50%)',
    maxWidth: '444px',
    maxHeight: '358px',
    overflow: 'hidden',
    background: '#F0F0F0',
    borderRadius: '4px',
    padding: '50px 32px 23px 32px',
  },
};

export const customStylesMobile: Styles = {
  ...customStylesDashboard,
  overlay: {
    ...(customStylesDashboard?.overlay || {}),
    backgroundColor: 'rgba(0, 0, 0, 0.25)',
    overflowY: 'scroll' as const,
    overflowX: 'hidden' as const,
  },
  content: {
    ...(customStylesDashboard?.content || {}),
    transform: 'none',
    inset: '20% 11%',
    width: '320px',
    height: '378px',
    top: '0',
    left: '0',
    right: '0',
    bottom: '0',
    padding: '90px 16px 32px 16px',
    margin: '0',
    borderRadius: '0',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignSelf: 'center',
  },
};

const StateModal: FC<StateModalProps> = ({ isOpen, onClose }) => {
  const { userInfo } = useAppSelector(selectAuth);
  const screenSize = useWindowSize();
  const isMobile = useMemo(() => screenSize.width < 768, [screenSize]);
  const currentState = useAppSelector(selectCurrentPatientState) as Record<number, string>;
  const dispatch = useAppDispatch();
  // Show what was detected or stored, but when neither knows, leave the
  // field empty instead of pre-selecting a state. This modal is the gate in
  // front of case creation, and the answer is written onto the case
  // permanently: a pre-filled state that the patient confirms without
  // reading is indistinguishable from one they chose, which is how cases
  // end up filed under a state the patient has never been in. Save stays
  // disabled until they pick, so the first real write is their own choice.
  const getSelectedPatientLocationState = (): string => currentState[userInfo.id] || getCurrentPState(userInfo.id) || '';

  const [state, setState] = useState<string>(() => getSelectedPatientLocationState());

  const onConfirm = (): void => {
    if (!state) return;

    dispatch(setPatientState({ userId: userInfo.id, state }));
    setCurrentPatientState(userInfo.id, state);
    onClose();
  };

  useEffect(() => {
    if (isOpen) {
      setState(getSelectedPatientLocationState());
    }
  }, [currentState[userInfo.id], isOpen, userInfo]);

  return (
    <Modal
      isOpen={isOpen}
      shouldCloseOnOverlayClick={false}
      onRequestClose={onClose}
      style={isMobile ? customStylesMobile : customStyles}
    >
      <div className={styles.content}>
        <div className={styles.stateSectionHeaderClose}>
          <img src={closeIcon} onClick={onClose} alt="close" />
        </div>
        <div className={styles.stateSection}>
          <span className={`fs-unmask ${styles.stateSectionHeader}`}>Confirm location</span>
          <span className={`fs-unmask ${styles.stateSectionSubTitle}`}>
            We need to know your location during the time of service to match you with a licensed dentist.
          </span>
          <div className={styles.stateSectionInput}>
            <MUISelectInput
              name="state"
              onChange={(value: any) => {
                setState(value);
              }}
              value={state}
              disablePortal={false}
              required={false}
              inputClass={`${styles.profileInputClass} fs-exclude`}
              wrapperClassName={`fs-unmask ${styles.completeProfileClass}`}
              options={stateOptionsWithoutAbbreviation}
            />
          </div>
        </div>
        <div className={styles.buttonsSection}>
          <div className={styles.save}>
            <Button className="fs-unmask" variant="contained" onClick={onConfirm} disabled={!state}>
              Save Changes
            </Button>
          </div>
          <div className={styles.cancel}>
            <Button className="fs-unmask" variant="text" onClick={onClose}>
              Cancel
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  );
};

Modal.setAppElement('#root');

export default StateModal;
